import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/stores/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getOwnerWallet,
  getOwnerWalletBanks,
  getOwnerWalletTransactions,
  getOwnerWalletTransfers,
  getOwnerWalletTransaction,
  postOwnerWalletCreate,
  postOwnerWalletQuote,
  postOwnerWalletResolveAccount,
  postOwnerWalletTransfer,
  postOwnerWalletTransferWallet,
  getSchoolWithdrawals,
  postSchoolWithdrawal,
  approveOwnerWithdrawal,
  rejectOwnerWithdrawal,
  getSchoolBankAccounts,
  postSchoolBankAccount,
  postSchoolBankAccountVerify,
  approveSchoolBankAccount,
  deleteSchoolBankAccount,
  postPaymentAccountsProvision,
  postAuthPinSet,
  getWalletExistingCustomers,
  postWalletLink,
  postOwnerWalletUnlink,
  getWalletFindAccount,
  getProfile,
  getAuthMe,
  postAuthPinChange,
} from '@/api/owner';
import {
  Wallet, Plus, X, AlertCircle, Loader2, RefreshCw, ArrowDownUp,
  TrendingUp, TrendingDown, Banknote, Building2, CreditCard, Eye,
  Download, Check, Ban, Send, ChevronRight, ShieldCheck,
} from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;
const money = (n: any) => {
  if (n == null || n === '') return '—';
  return '₦' + Number(n).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

type Tab = 'overview' | 'transactions' | 'transfers' | 'bank' | 'withdrawals';

interface Props {
  initialTab?: Tab;
}

export function OwnerWalletPage({ initialTab = 'overview' }: Props) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [showCreateWallet, setShowCreateWallet] = useState(false);
  const [showTransferBank, setShowTransferBank] = useState(false);
  const [showTransferWallet, setShowTransferWallet] = useState(false);
  const [showAddBank, setShowAddBank] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [openTx, setOpenTx] = useState<number | null>(null);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [showLinkExisting, setShowLinkExisting] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [banksLoadFailed, setBanksLoadFailed] = useState(false);

  // Unified prompt modals — replace native prompt()/confirm()/alert()
  const [pinRequest, setPinRequest] = useState<{ title: string; resolve: (pin: string | null) => void } | null>(null);
  const [confirmRequest, setConfirmRequest] = useState<{ title: string; message: string; confirmLabel?: string; resolve: (ok: boolean) => void } | null>(null);
  const [textRequest, setTextRequest] = useState<{ title: string; label: string; placeholder?: string; resolve: (text: string | null) => void } | null>(null);

  const requestPin = (title: string) => new Promise<string | null>((resolve) => setPinRequest({ title, resolve }));
  const requestConfirm = (title: string, message: string, confirmLabel?: string) =>
    new Promise<boolean>((resolve) => setConfirmRequest({ title, message, confirmLabel, resolve }));
  const requestText = (title: string, label: string, placeholder?: string) =>
    new Promise<string | null>((resolve) => setTextRequest({ title, label, placeholder, resolve }));

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // Queries
  // Ask XpressWallet for the live balance on first load and when Refresh is pressed; the minute-by-minute
  // refresh uses the saved balance (the server re-reads it when it is older than 30 seconds).
  const forceLive = useRef(true);
  const walletQ = useQuery({
    queryKey: ['owner-wallet'],
    refetchInterval: 60000,
    queryFn: () => { const live = forceLive.current; forceLive.current = false; return getOwnerWallet(live ? { refresh: true } : {}).then(unwrap); },
    retry: 1,
  });
  const transactionsQ = useQuery({
    queryKey: ['owner-wallet-tx', tab],
    queryFn: () => getOwnerWalletTransactions({ per_page: 50 }).then(unwrap),
    enabled: !!walletQ.data && tab === 'transactions',
  });
  const transfersQ = useQuery({
    queryKey: ['owner-wallet-transfers', tab],
    queryFn: () => getOwnerWalletTransfers({ per_page: 50 }).then(unwrap),
    enabled: !!walletQ.data && tab === 'transfers',
  });
  const banksQ = useQuery({
    queryKey: ['owner-wallet-banks'],
    queryFn: () => getOwnerWalletBanks().then(unwrap),
    enabled: !!walletQ.data,
  });
  const bankAccountsQ = useQuery({
    queryKey: ['owner-bank-accounts'],
    queryFn: () => getSchoolBankAccounts({ per_page: 50 }).then(unwrap),
    enabled: !!walletQ.data && (tab === 'bank' || tab === 'withdrawals'),
  });
  const withdrawalsQ = useQuery({
    queryKey: ['owner-withdrawals', tab],
    queryFn: () => getSchoolWithdrawals({ per_page: 50 }).then(unwrap),
    enabled: !!walletQ.data && tab === 'withdrawals',
  });
  const meQ = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => getAuthMe().then(unwrap),
    staleTime: 60_000,
  });
  // /auth/me returns { flags: { pin_set: bool, ... } }
  const hasPin = !!(meQ.data?.flags?.pin_set ?? meQ.data?.has_pin ?? false);

  const isOwner = useAuth((x) => x.user?.role) === 'owner';
  const profileQ = useQuery({
    queryKey: ['owner-profile'],
    queryFn: () => getProfile().then(unwrap),
    enabled: isOwner,
  });

  const wallet = walletQ.data;
  const transactions = arr<any>(transactionsQ.data?.data ?? transactionsQ.data);
  const transfers = arr<any>(transfersQ.data?.data ?? transfersQ.data);
  const banks = arr<any>(banksQ.data?.banks ?? banksQ.data);
  const bankAccounts = arr<any>(bankAccountsQ.data?.data ?? bankAccountsQ.data);
  const withdrawals = arr<any>(withdrawalsQ.data?.data ?? withdrawalsQ.data);
  const profile = profileQ.data;

  // "Create wallet" only when the server says there is none; a failed or slow request never looks like a missing wallet.
  const noWallet = !!wallet && wallet.exists === false && !wallet.account_number;
  const hasWallet = !!wallet && !noWallet;
  const loadFailed = !wallet && walletQ.isError;

  // Aggregated stats
  const stats = useMemo(() => {
    const balance = Number(wallet?.available_balance ?? wallet?.balance ?? 0);
    const thisIn = Number(wallet?.this_month?.in ?? 0);
    const thisOut = Number(wallet?.this_month?.out ?? 0);
    const pending = Number(wallet?.pending_withdrawals ?? 0);
    const pendingCount = Number(wallet?.pending_withdrawals_count ?? 0);
    return { balance, thisIn, thisOut, pending, pendingCount };
  }, [wallet]);

  const refreshWallet = () => {
    forceLive.current = true;
    walletQ.refetch();
    transactionsQ.refetch();
    transfersQ.refetch();
    bankAccountsQ.refetch();
    withdrawalsQ.refetch();
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Finance</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Wallet</h1>
          <p className="text-slate-500 text-sm mt-1">
            School wallet · transfers, withdrawals, and virtual accounts
          </p>
        </div>
        <div className="flex gap-2">
          {hasWallet && (
            <>
              <button onClick={refreshWallet}
                className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
              <button onClick={() => setShowTransferWallet(true)}
                className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1">
                <ArrowDownUp className="w-4 h-4" /> To wallet
              </button>

              {walletQ.data?.exists && isOwner && (
                <button
                  onClick={() => setUnlinkOpen(true)}
                  className="px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 inline-flex items-center gap-1"
                >
                  Unlink Xpress
                </button>
              )}
              <button onClick={() => setShowTransferBank(true)}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1">
                <Building2 className="w-4 h-4" /> To bank
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Banks load failure warning */}
      {hasWallet && banksQ.isError && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <b>Could not load bank list.</b>{' '}
            {(banksQ.error as any)?.response?.data?.message ?? 'The XpressWallet service may not be configured.'}
          </div>
          <button onClick={() => banksQ.refetch()}
            className="text-xs font-semibold text-amber-700 hover:underline">Retry</button>
        </div>
      )}

      {/* Success banner (replaces alert for actions like PIN, invoice, transfer) */}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-start gap-2">
          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">{successMsg}</div>
          <button onClick={() => setSuccessMsg(null)} className="text-green-700 hover:text-green-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loadFailed && (
        <div className="bg-white p-12 rounded-xl border border-amber-200 text-center">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="font-bold text-slate-900 text-lg">We couldn’t load your wallet just now</p>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Your wallet and money are safe. The wallet provider was slow to answer — try again in a moment.</p>
          <button onClick={refreshWallet} className="mt-4 px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      )}

      {/* No wallet state */}
      {noWallet && !walletQ.isLoading && (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-bold text-slate-900 text-lg">Create your school wallet</p>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            The wallet collects all school payments (fees, receipts, invoices) and lets you transfer
            to bank accounts or other schools. One-time setup with BVN/NIN verification.
          </p>
          {isOwner ? <button onClick={() => setShowCreateWallet(true)}
            className="mt-4 px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Create wallet
          </button> : <p className="mt-4 text-sm font-semibold text-amber-700">Only the school owner can create the wallet.</p>}
        </div>
      )}

      {walletQ.isLoading && (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
          <p className="text-sm text-slate-500 mt-2">Loading wallet…</p>
        </div>
      )}

      {hasWallet && (
        <>
          {/* Tabs */}
          <div className="border-b border-slate-200">
            <div className="flex gap-1 overflow-x-auto">
              {([
                { key: 'overview' as Tab, label: 'Overview', icon: Wallet },
                { key: 'transactions' as Tab, label: 'Transactions', icon: Banknote },
                { key: 'transfers' as Tab, label: 'Transfers', icon: ArrowDownUp },
                { key: 'bank' as Tab, label: 'Bank accounts', icon: Building2, count: bankAccounts.length },
                { key: 'withdrawals' as Tab, label: 'Withdrawals', icon: CreditCard, count: stats.pendingCount },
              ]).map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)}
                    className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 inline-flex items-center gap-2 transition ${
                      tab === t.key ? 'border-[#0f172a] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}>
                    <Icon className="w-4 h-4" /> {t.label}
                    {t.count != null && t.count > 0 && (
                      <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">{t.count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── OVERVIEW TAB ── */}
          {tab === 'overview' && (
            <div className="space-y-6">
              {/* Warning: wallet row exists but not fully onboarded */}
              {!wallet?.account_number && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-900">Wallet not fully set up</p>
                      <p className="text-sm text-amber-800 mt-1">
                        This school doesn't have an Xpress account number yet. Link an existing Xpress account or create a new one to unlock transfers, withdrawals, and payroll payouts.
                      </p>
                      {isOwner && <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={() => setShowLinkExisting(true)}
                          className="px-4 py-2 text-sm font-semibold text-white bg-amber-700 rounded-lg hover:bg-amber-800 inline-flex items-center gap-2">
                          <ArrowDownUp className="w-4 h-4" /> Link existing Xpress account
                        </button>
                        <button
                          onClick={() => setShowCreateWallet(true)}
                          className="px-4 py-2 text-sm font-semibold text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 inline-flex items-center gap-2">
                          <Plus className="w-4 h-4" /> Create new wallet
                        </button>
                      </div>}
                    </div>
                  </div>
                </div>
              )}
              {/* Hero balance card */}
              <div className="bg-gradient-to-br from-[#0f172a] to-slate-700 text-white rounded-2xl p-8 shadow-xl">
                <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-2">Available balance</p>
                <p className="text-5xl font-extrabold font-mono">{money(stats.balance)}</p>
                <div className="flex gap-6 mt-6 text-sm">
                  <div>
                    <p className="opacity-70 text-xs">Account number</p>
                    <p className="font-mono font-semibold">{wallet.account_number ?? '—'}</p>
                  </div>
                  <div>
                    <p className="opacity-70 text-xs">Bank</p>
                    <p className="font-semibold">{wallet.bank_name ?? 'XpressWallet'}</p>
                  </div>
                  <div>
                    <p className="opacity-70 text-xs">Status</p>
                    <p className="font-semibold capitalize">{wallet.status ?? 'active'}</p>
                  </div>
                </div>
                {stats.pending > 0 && (
                  <div className="mt-4 bg-white/10 border border-white/20 rounded-lg p-3 text-sm">
                    <b>{money(stats.pending)}</b> is pending in {stats.pendingCount} withdrawal{stats.pendingCount === 1 ? '' : 's'}
                  </div>
                )}
              </div>

              {/* This-month stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <p className="text-xs font-semibold text-slate-500">Money in · this month</p>
                  </div>
                  <p className="text-2xl font-extrabold text-green-700 font-mono">{money(stats.thisIn)}</p>
                </div>
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4 text-red-600" />
                    <p className="text-xs font-semibold text-slate-500">Money out · this month</p>
                  </div>
                  <p className="text-2xl font-extrabold text-red-700 font-mono">{money(stats.thisOut)}</p>
                </div>
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-amber-600" />
                    <p className="text-xs font-semibold text-slate-500">Pending withdrawals</p>
                  </div>
                  <p className="text-2xl font-extrabold text-amber-700 font-mono">{money(stats.pending)}</p>
                  <p className="text-xs text-slate-500 mt-1">{stats.pendingCount} request{stats.pendingCount === 1 ? '' : 's'}</p>
                </div>
              </div>

              {/* Quick actions */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h2 className="font-bold text-slate-900 mb-4">Quick actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button onClick={() => setShowTransferBank(true)}
                    className="p-4 text-center border border-slate-200 rounded-xl hover:border-slate-400 hover:bg-slate-50 transition">
                    <Building2 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-slate-900">Transfer to bank</p>
                    <p className="text-xs text-slate-500 mt-0.5">Send money out</p>
                  </button>
                  <button onClick={() => setShowTransferWallet(true)}
                    className="p-4 text-center border border-slate-200 rounded-xl hover:border-slate-400 hover:bg-slate-50 transition">
                    <ArrowDownUp className="w-6 h-6 text-violet-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-slate-900">Transfer to wallet</p>
                    <p className="text-xs text-slate-500 mt-0.5">Another NUBAN</p>
                  </button>
                  <button onClick={() => setShowWithdraw(true)} disabled={bankAccounts.filter((b: any) => b.verified).length === 0}
                    className="p-4 text-center border border-slate-200 rounded-xl hover:border-slate-400 hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed">
                    <CreditCard className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-slate-900">Withdraw</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {bankAccounts.filter((b: any) => b.verified).length === 0 ? 'Add bank first' : 'To saved bank'}
                    </p>
                  </button>
                  <button onClick={() => setShowAddBank(true)}
                    className="p-4 text-center border border-slate-200 rounded-xl hover:border-slate-400 hover:bg-slate-50 transition">
                    <Plus className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-slate-900">Add bank</p>
                    <p className="text-xs text-slate-500 mt-0.5">For withdrawals</p>
                  </button>
                </div>
              </div>

              {/* PIN setup — Set when none, Change when set */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-slate-700 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">Transaction PIN</p>
                    <p className="text-sm text-slate-600 mt-1">
                      {hasPin
                        ? 'Your PIN is set. You can change it anytime — you will need your current PIN.'
                        : 'A 4-digit PIN is required to authorise transfers, withdrawals, and payroll payments. Set it now to unlock these actions.'}
                    </p>
                    <button
                      onClick={() => setShowPinSetup(true)}
                      className="mt-3 px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800">
                      {hasPin ? 'Change PIN' : 'Set PIN'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── TRANSACTIONS TAB ── */}
          {tab === 'transactions' && (
            <TransactionsTab
              loading={transactionsQ.isLoading}
              transactions={transactions}
              onOpenTx={(id) => setOpenTx(id)}
            />
          )}

          {/* ── TRANSFERS TAB ── */}
          {tab === 'transfers' && (
            <TransfersTab
              loading={transfersQ.isLoading}
              transfers={transfers}
            />
          )}

          {/* ── BANK ACCOUNTS TAB ── */}
          {tab === 'bank' && (
            <BankAccountsTab
              loading={bankAccountsQ.isLoading}
              accounts={bankAccounts}
              onAdd={() => setShowAddBank(true)}
              onApprove={async (id) => {
                try {
                  await approveSchoolBankAccount(id);
                  setSuccessMsg('Bank account approved.');
                } catch (e: any) {
                  const msg = e?.response?.data?.message ?? 'Approve failed';
                  // Backend returns 422 'Already approved.' when verified_at is already set.
                  // Treat as success so the UI reflects reality instead of showing a red error.
                  if (msg.toLowerCase().includes('already approved')) {
                    setSuccessMsg('Bank account is already approved.');
                  } else {
                    setError(msg);
                  }
                } finally {
                  bankAccountsQ.refetch();
                }
              }}
              onDelete={async (id) => {
                const ok = await requestConfirm('Delete bank account?', 'Any pending withdrawals to this account may fail.');
                if (!ok) return;
                try {
                  await deleteSchoolBankAccount(id);
                  bankAccountsQ.refetch();
                  setSuccessMsg('Bank account deleted.');
                } catch (e: any) {
                  setError(e?.response?.data?.message ?? 'Delete failed');
                }
              }}
            />
          )}

          {/* ── WITHDRAWALS TAB ── */}
          {tab === 'withdrawals' && (
            <WithdrawalsTab
              loading={withdrawalsQ.isLoading}
              withdrawals={withdrawals}
              hasVerifiedBank={bankAccounts.some((b: any) => b.verified)}
              onRequest={() => {
                if (!bankAccounts.some((b: any) => b.verified)) {
                  setError('Add and verify a bank account first (Bank accounts tab).');
                  return;
                }
                setShowWithdraw(true);
              }}
              onApprove={async (id) => {
                const pinResp = await requestPin('Approve withdrawal');
                if (pinResp === null) return;
                try {
                  await approveOwnerWithdrawal(id, pinResp);
                  withdrawalsQ.refetch();
                  walletQ.refetch();
                  setSuccessMsg('Withdrawal approved.');
                } catch (e: any) {
                  setError(e?.response?.data?.message ?? 'Approve failed');
                }
              }}
              onReject={async (id) => {
                const reason = await requestText('Reject withdrawal', 'Reason (required)', 'e.g. Incorrect bank details');
                if (!reason || !reason.trim()) return;
                try {
                  await rejectOwnerWithdrawal(id, reason.trim());
                  withdrawalsQ.refetch();
                  setSuccessMsg('Withdrawal rejected.');
                } catch (e: any) {
                  setError(e?.response?.data?.message ?? 'Reject failed');
                }
              }}
            />
          )}
        </>
      )}

      {/* Modals */}
      {showCreateWallet && (
        <CreateWalletModal
          profile={profile}
          onClose={() => setShowCreateWallet(false)}
          onSuccess={() => {
            setShowCreateWallet(false);
            walletQ.refetch();
            setSuccessMsg('Wallet created — you can now send and receive money.');
          }}
          onError={(m) => setError(m)}
        />
      )}
      {showTransferBank && hasWallet && (
        <TransferBankModal
          banks={banks}
          available={stats.balance}
          onClose={() => setShowTransferBank(false)}
          onSuccess={(m?: string) => {
            setShowTransferBank(false);
            refreshWallet();
            setSuccessMsg(m ?? 'Transfer sent to bank.');
          }}
          onError={(m) => setError(m)}
          requestPin={requestPin}
        />
      )}
      {showTransferWallet && hasWallet && (
        <TransferWalletModal
          available={stats.balance}
          onClose={() => setShowTransferWallet(false)}
          onSuccess={(m?: string) => {
            setShowTransferWallet(false);
            refreshWallet();
            setSuccessMsg(m ?? 'Transfer sent.');
          }}
          onError={(m) => setError(m)}
          requestPin={requestPin}
        />
      )}
      {showAddBank && hasWallet && (
        <AddBankModal
          banks={banks}
          onClose={() => setShowAddBank(false)}
          onSuccess={() => {
            setShowAddBank(false);
            bankAccountsQ.refetch();
            setSuccessMsg('Bank account saved. Verify it in the Bank accounts tab to use it for withdrawals.');
          }}
          onError={(m) => setError(m)}
        />
      )}
      {showWithdraw && hasWallet && (
        <RequestWithdrawalModal
          bankAccounts={bankAccounts.filter((b: any) => b.verified)}
          available={stats.balance - stats.pending}
          onClose={() => setShowWithdraw(false)}
          onSuccess={(m?: string) => {
            setShowWithdraw(false);
            withdrawalsQ.refetch();
            walletQ.refetch();
            setSuccessMsg(m ?? 'Withdrawal requested.');
          }}
          onError={(m) => setError(m)}
          requestPin={requestPin}
        />
      )}
      {openTx && (
        <TransactionDetailModal
          id={openTx}
          onClose={() => setOpenTx(null)}
        />
      )}
      {showLinkExisting && (
        <LinkExistingWalletModal
          onClose={() => setShowLinkExisting(false)}
          onSuccess={() => {
            setShowLinkExisting(false);
            qc.invalidateQueries({ queryKey: ['owner-wallet'] });
            qc.invalidateQueries({ queryKey: ['owner-wallet-tx'] });
            qc.invalidateQueries({ queryKey: ['owner-wallet-transfers'] });
            qc.invalidateQueries({ queryKey: ['owner-bank-accounts'] });
            qc.invalidateQueries({ queryKey: ['owner-withdrawals'] });
            setSuccessMsg('Xpress wallet linked. Balance synced from Xpress.');
          }}
          onError={(m) => setError(m)}
        />
      )}

      {/* Toast notifications */}
      {error && (
        <div className="fixed top-4 right-4 z-[100] max-w-md">
          <div className="bg-red-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3">
            <p className="text-sm font-semibold flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      {successMsg && (
        <div className="fixed top-4 right-4 z-[100] max-w-md">
          <div className="bg-green-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3">
            <p className="text-sm font-semibold flex-1">{successMsg}</p>
            <button onClick={() => setSuccessMsg(null)} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {pinRequest && (
        <PinModal title={pinRequest.title}
          onClose={() => { pinRequest.resolve(null); setPinRequest(null); }}
          onConfirm={(pin) => { pinRequest.resolve(pin); setPinRequest(null); }} />
      )}
      {confirmRequest && (
        <ConfirmModal title={confirmRequest.title} message={confirmRequest.message}
          confirmLabel={confirmRequest.confirmLabel}
          onClose={() => { confirmRequest.resolve(false); setConfirmRequest(null); }}
          onConfirm={() => { confirmRequest.resolve(true); setConfirmRequest(null); }} />
      )}
      {textRequest && (
        <TextPromptModal title={textRequest.title} label={textRequest.label}
          placeholder={textRequest.placeholder} required
          onClose={() => { textRequest.resolve(null); setTextRequest(null); }}
          onSubmit={(t) => { textRequest.resolve(t); setTextRequest(null); }} />
      )}

      {unlinkOpen && (
        <UnlinkXpressModal
          accountNumber={walletQ.data?.account_number ?? ""}
          balance={Number(walletQ.data?.balance ?? 0)}
          onClose={() => setUnlinkOpen(false)}
          onSuccess={() => {
            setUnlinkOpen(false);
            qc.invalidateQueries({ queryKey: ["owner-wallet"] });
            qc.invalidateQueries({ queryKey: ["owner-wallet-tx"] });
            walletQ.refetch();
          }}
        />
      )}
      {showPinSetup && (
        <PinSetupModal
          hasPin={hasPin}
          onClose={() => setShowPinSetup(false)}
          onSuccess={() => {
            setShowPinSetup(false);
            meQ.refetch();
            setSuccessMsg(hasPin ? 'PIN updated.' : 'PIN saved. You can now authorise transfers and withdrawals.');
          }}
          onError={(m) => setError(m)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUB-TABS
// ═══════════════════════════════════════════════════════════════

function TransactionsTab({ loading, transactions, onOpenTx }: {
  loading: boolean; transactions: any[]; onOpenTx: (id: number) => void;
}) {
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const filtered = transactions.filter((t: any) => {
    if (typeFilter && t.type !== typeFilter) return false;
    if (statusFilter && t.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(String(t.reference ?? '').toLowerCase().includes(s) || String(t.narration ?? '').toLowerCase().includes(s))) return false;
    }
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search reference or narration…"
          className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-3 py-2 text-sm" />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All types</option>
          <option value="credit">Credits only</option>
          <option value="debit">Debits only</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All statuses</option>
          <option value="successful">Successful</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <Banknote className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No transactions</p>
          <p className="text-sm text-slate-500 mt-1">
            {transactions.length === 0
              ? 'Once parents pay fees or you make transfers, transactions appear here.'
              : 'No transactions match your filter.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">Reference</th>
                <th className="px-3 py-3 text-left">Narration</th>
                <th className="px-3 py-3 text-left">Date</th>
                <th className="px-3 py-3 text-right">Amount</th>
                <th className="px-3 py-3 text-left">Status</th>
                <th className="px-3 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((t: any) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{t.reference ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-900">{t.narration ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${
                    t.type === 'credit' ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {t.type === 'credit' ? '+' : '-'}{money(t.amount)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${
                      t.status === 'successful' ? 'bg-green-50 text-green-700 border-green-200' :
                      t.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onOpenTx(t.id)}
                      className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TransfersTab({ loading, transfers }: { loading: boolean; transfers: any[] }) {
  const [kind, setKind] = useState<'all' | 'bank' | 'wallet'>('all');
  const filtered = transfers.filter((t: any) => kind === 'all' || t.kind === kind);

  return (
    <div className="space-y-4">
      <div className="inline-flex bg-slate-100 rounded-lg p-1">
        {(['all', 'bank', 'wallet'] as const).map(k => (
          <button key={k} onClick={() => setKind(k)}
            className={`px-4 py-1.5 text-sm font-bold rounded-md transition ${
              kind === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
            }`}>
            {k === 'all' ? 'All' : k === 'bank' ? 'To bank' : 'To wallet'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <ArrowDownUp className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No transfers</p>
          <p className="text-sm text-slate-500 mt-1">
            Bank and wallet transfers appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">Reference</th>
                <th className="px-3 py-3 text-left">Destination</th>
                <th className="px-3 py-3 text-left">Date</th>
                <th className="px-3 py-3 text-right">Amount</th>
                <th className="px-3 py-3 text-right">Fee</th>
                <th className="px-3 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((t: any) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{t.reference}</td>
                  <td className="px-3 py-2">
                    <p className="text-slate-900 font-medium">
                      {t.kind === 'bank' ? (t.bank_name ?? 'Bank') : 'Wallet'}
                    </p>
                    <p className="text-xs text-slate-500 font-mono">
                      {t.account_number} · {t.account_name}
                    </p>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                  <td className="px-3 py-2 text-right font-mono">{money(t.amount)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-500">{money(t.fee)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${
                      t.status === 'successful' ? 'bg-green-50 text-green-700 border-green-200' :
                      t.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {t.status}
                    </span>
                    {t.failure_reason && (
                      <p className="text-xs text-red-600 mt-0.5">{t.failure_reason}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BankAccountsTab({ loading, accounts, onAdd, onApprove, onDelete }: {
  loading: boolean;
  accounts: any[];
  onAdd: () => void;
  onApprove: (id: number) => void;
  onDelete: (id: number) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={onAdd}
          className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add bank account
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No bank accounts yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Add a bank account to enable withdrawals. Owner-added accounts are verified instantly.
          </p>
          <button onClick={onAdd}
            className="mt-4 px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add first bank account
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accounts.map((a: any) => (
            <div key={a.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <p className="font-bold text-slate-900">{a.bank_name ?? '—'}</p>
                  <p className="text-xs text-slate-500 font-mono mt-1">{a.account_number}</p>
                  <p className="text-sm text-slate-700 mt-1">{a.account_name}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {a.is_default && (
                    <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                      DEFAULT
                    </span>
                  )}
                  {a.verified ? (
                    <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full border border-green-200 inline-flex items-center gap-1">
                      <Check className="w-2.5 h-2.5" /> Verified
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full border border-amber-200">
                      Pending approval
                    </span>
                  )}
                  {a.purpose && (
                    <span className="text-[10px] text-slate-500 capitalize">{a.purpose}</span>
                  )}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                {!a.verified && (
                  <button onClick={() => onApprove(a.id)}
                    className="text-xs font-semibold text-white bg-green-600 hover:bg-green-700 px-2 py-1 rounded inline-flex items-center gap-1">
                    <Check className="w-3 h-3" /> Approve
                  </button>
                )}
                <button onClick={() => onDelete(a.id)}
                  className="text-xs font-semibold text-red-700 bg-white border border-red-300 hover:bg-red-50 px-2 py-1 rounded inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WithdrawalsTab({ loading, withdrawals, hasVerifiedBank, onRequest, onApprove, onReject }: {
  loading: boolean;
  withdrawals: any[];
  hasVerifiedBank: boolean;
  onRequest: () => void;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
}) {
  const statusTone: Record<string, string> = {
    pending_approval: 'bg-amber-50 text-amber-800 border-amber-200',
    processing:       'bg-blue-50 text-blue-700 border-blue-200',
    successful:       'bg-green-50 text-green-700 border-green-200',
    rejected:         'bg-red-50 text-red-700 border-red-200',
    failed:           'bg-red-50 text-red-700 border-red-200',
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={onRequest} disabled={!hasVerifiedBank}
          className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-1">
          <Plus className="w-4 h-4" />
          {hasVerifiedBank ? 'Request withdrawal' : 'Add verified bank first'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
        </div>
      ) : withdrawals.length === 0 ? (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No withdrawals yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Withdraw money from your wallet to a verified bank account.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">Reference</th>
                <th className="px-3 py-3 text-left">Bank</th>
                <th className="px-3 py-3 text-left">Requested by</th>
                <th className="px-3 py-3 text-left">Date</th>
                <th className="px-3 py-3 text-right">Amount</th>
                <th className="px-3 py-3 text-right">Fee</th>
                <th className="px-3 py-3 text-left">Status</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {withdrawals.map((w: any) => (
                <tr key={w.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{w.reference ?? '—'}</td>
                  <td className="px-3 py-2">
                    <p className="text-slate-900 font-medium">{w.bank_name ?? w.bankAccount?.bank_name ?? '—'}</p>
                    <p className="text-xs text-slate-500 font-mono">
                      {w.account_number ?? w.bankAccount?.account_number}
                    </p>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {w.requestedBy?.name ?? w.requested_by_name ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(w.created_at)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{money(w.amount)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-500">{money(w.fee)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${statusTone[w.status] ?? 'bg-slate-100 text-slate-600'}`}>
                      {(w.status ?? '').replace('_', ' ')}
                    </span>
                    {w.failure_reason && (
                      <p className="text-xs text-red-600 mt-0.5">{w.failure_reason}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {w.status === 'pending_approval' && (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => onApprove(w.id)}
                          className="text-xs font-semibold text-green-700 hover:underline inline-flex items-center gap-1">
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button onClick={() => onReject(w.id)}
                          className="text-xs font-semibold text-red-600 hover:underline inline-flex items-center gap-1">
                          <Ban className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODALS
// ═══════════════════════════════════════════════════════════════

function CreateWalletModal({ profile, onClose, onSuccess, onError }: {
  profile: any;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [bvn, setBvn] = useState('');
  const [nin, setNin] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [phone, setPhone] = useState(profile?.phone ?? '');
  const [email, setEmail] = useState(profile?.email ?? '');
  const [address, setAddress] = useState(profile?.address ?? '');
  const [businessName, setBusinessName] = useState(profile?.name ?? '');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const cleanBvn = bvn.replace(/\D/g, '');
    const cleanNin = nin.replace(/\D/g, '');
    if (cleanBvn.length !== 11) return onError(`BVN must be 11 digits (you have ${cleanBvn.length})`);
    if (cleanNin.length !== 11) return onError(`NIN must be 11 digits (you have ${cleanNin.length})`);
    if (!firstName.trim() || !lastName.trim()) return onError('First and last name required');
    if (!dob) return onError('Date of birth required');
    if (!email.trim() || !phone.trim() || !address.trim()) return onError('Phone, email, address required');
    setSubmitting(true);
    try {
      await postOwnerWalletCreate({
        bvn: cleanBvn, nin: cleanNin, first_name: firstName.trim(), last_name: lastName.trim(),
        date_of_birth: dob, phone_number: phone.trim(), email: email.trim(),
        address: address.trim(), business_name: businessName.trim() || undefined,
      });
      onSuccess();
    } catch (e: any) {
      const msg = e?.response?.data?.message
        || (e?.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' · '))
        || 'Wallet creation failed';
      onError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Create school wallet</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <b>BVN and NIN</b> are sent directly to XpressWallet for KYC. Never stored in our database.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name *" value={firstName} onChange={setFirstName} />
              <Field label="Last name *" value={lastName} onChange={setLastName} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="BVN * (11 digits)" value={bvn} onChange={(v) => setBvn(v.replace(/\D/g, '').slice(0, 11))} inputMode="numeric" />
              <Field label="NIN * (11 digits)" value={nin} onChange={(v) => setNin(v.replace(/\D/g, '').slice(0, 11))} inputMode="numeric" />
            </div>
            <Field label="Date of birth *" value={dob} onChange={setDob} type="date" />
            <Field label="Phone *" value={phone} onChange={setPhone} />
            <Field label="Email *" value={email} onChange={setEmail} type="email" />
            <Field label="Address *" value={address} onChange={setAddress} />
            <Field label="Business name" value={businessName} onChange={setBusinessName} />
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">
              Cancel
            </button>
            <button onClick={submit} disabled={submitting}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {submitting ? 'Creating…' : 'Create wallet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TransferBankModal({ banks, available, onClose, onSuccess, onError, requestPin }: {
  banks: any[]; available: number; onClose: () => void; onSuccess: (msg?: string) => void; onError: (msg: string) => void;
  requestPin: (title: string) => Promise<string | null>;
}) {
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [amount, setAmount] = useState('');
  const [narration, setNarration] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [quote, setQuote] = useState<any>(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [letterFilter, setLetterFilter] = useState<string>('');
  const [manualEntry, setManualEntry] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [triedKey, setTriedKey] = useState<string>('');

  const filteredBanks = banks.filter((b: any) => {
    const name = String(b.name ?? '').toUpperCase();
    if (letterFilter && !name.startsWith(letterFilter)) return false;
    if (bankSearch) {
      const q = bankSearch.toUpperCase();
      if (!name.includes(q) && !String(b.code ?? '').includes(q)) return false;
    }
    return true;
  });

  const selectedBank = banks.find((b: any) => b.code === bankCode);

  const verify = async () => {
    if (!bankCode) return;
    if (accountNumber.length !== 10) return;
    setVerifying(true);
    setVerifyMsg(null);
    setAccountName('');
    setManualEntry(false);
    try {
      const r: any = await postOwnerWalletResolveAccount({ bank_code: bankCode, account_number: accountNumber });
      const d = unwrap(r);
      const name = d?.account_name ?? d?.name ?? null;
      if (name) {
        setAccountName(name);
        setVerifyMsg('Verified: ' + name);
      } else if (d?.manual_entry) {
        setManualEntry(true);
        setVerifyMsg(d?.message ?? 'Type the account name manually.');
      } else {
        setVerifyMsg(d?.message ?? 'Could not resolve - type name manually');
        setManualEntry(true);
      }
    } catch (e: any) {
      setVerifyMsg(e?.response?.data?.message ?? 'Account verification failed - type name manually');
      setManualEntry(true);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const key = bankCode + ':' + accountNumber;
    if (accountNumber.length === 10 && bankCode && !verifying && !accountName && !manualEntry && triedKey !== key) {
      setTriedKey(key);
      verify();
    }
  }, [accountNumber, bankCode]);

  const getQuote = async () => {
    const amt = Number(amount);
    if (!amt || amt < 100) return onError('Amount must be at least 100');
    setQuoting(true);
    try {
      const r: any = await postOwnerWalletQuote({ type: 'bank_transfer', amount: amt });
      setQuote(unwrap(r));
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Quote failed');
    } finally {
      setQuoting(false);
    }
  };

  const submit = async () => {
    if (!accountName) return onError('Enter the account holder name first');
    const amt = Number(amount);
    if (!amt || amt < 100) return onError('Amount must be at least 100');
    const pin = await requestPin('Send to bank');
    if (pin === null) return;
    setSubmitting(true);
    const idempotencyKey = 'tx-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
    try {
      const r: any = await postOwnerWalletTransfer({
        amount: amt,
        bank_code: bankCode,
        account_number: accountNumber,
        bank_name: selectedBank?.name,
        account_name: accountName,
        narration: narration.trim() || undefined,
      }, pin, idempotencyKey);
      onSuccess(r?.data?.message);
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Transfer to bank</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-3">
            <p className="text-xs text-slate-500">Available: <b className="text-slate-900 font-mono">{money(available)}</b></p>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Bank *</label>
              <input
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
                placeholder="Search bank name or code"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2"
              />
              <div className="flex flex-wrap gap-0.5 mb-2 select-none">
                <button
                  type="button"
                  onClick={() => setLetterFilter('')}
                  className={'px-2 py-0.5 text-xs font-bold rounded ' + (letterFilter === '' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                >
                  ALL
                </button>
                {['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'].map(L => (
                  <button
                    key={L}
                    type="button"
                    onClick={() => setLetterFilter(letterFilter === L ? '' : L)}
                    className={'w-6 h-6 text-xs font-bold rounded ' + (letterFilter === L ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                  >
                    {L}
                  </button>
                ))}
              </div>
              <select value={bankCode}
                onChange={(e) => { setBankCode(e.target.value); setAccountName(''); setManualEntry(false); setVerifyMsg(null); setTriedKey(''); setQuote(null); }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">
                  {filteredBanks.length === 0 ? 'No banks match - clear filters' : 'Choose from ' + filteredBanks.length + ' banks'}
                </option>
                {filteredBanks.map((b: any) => (
                  <option key={b.code ?? b.id} value={b.code}>{b.name} ({b.code})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Account number *</label>
              <div className="flex gap-2">
                <input value={accountNumber}
                  onChange={(e) => {
                    setAccountNumber(e.target.value.replace(/\D/g, ''));
                    setAccountName('');
                    setManualEntry(false);
                    setVerifyMsg(null);
                    setTriedKey('');
                    setQuote(null);
                  }}
                  maxLength={10} placeholder="10 digits"
                  className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
                <button onClick={verify}
                  disabled={verifying || !bankCode || accountNumber.length !== 10}
                  className="px-3 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50">
                  {verifying ? 'Verifying...' : 'Verify'}
                </button>
              </div>
              {verifyMsg && !manualEntry && accountName && (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 mt-2">{verifyMsg}</p>
              )}
              {verifyMsg && manualEntry && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">{verifyMsg}</p>
              )}
              {manualEntry && (
                <div className="mt-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Type the account name exactly as the bank shows it *
                  </label>
                  <input value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder="e.g. OBIORA PAUL NWOBODO"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Amount *</label>
              <input type="number" value={amount}
                onChange={(e) => { setAmount(e.target.value); setQuote(null); }}
                placeholder="Min 100"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Narration</label>
              <input value={narration} onChange={(e) => setNarration(e.target.value.slice(0, 60))}
                placeholder="Optional"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            {amount && !quote && (
              <button onClick={getQuote} disabled={quoting}
                className="text-xs font-semibold text-slate-700 hover:underline">
                {quoting ? 'Calculating fee...' : 'Get transfer fee'}
              </button>
            )}
            {quote && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-600">Amount</span><span className="font-mono">{money(quote.amount ?? amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Fee</span><span className="font-mono">{money(quote.fee ?? 0)}</span></div>
                <div className="flex justify-between font-bold border-t border-slate-300 pt-1"><span>Total</span><span className="font-mono">{money(quote.total ?? (Number(amount) + Number(quote.fee ?? 0)))}</span></div>
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
            <button onClick={submit} disabled={!accountName || !amount || submitting}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {submitting ? 'Sending...' : 'Send transfer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function TransferWalletModal({ available, onClose, onSuccess, onError, requestPin }: {
  available: number; onClose: () => void; onSuccess: (msg?: string) => void; onError: (msg: string) => void;
  requestPin: (title: string) => Promise<string | null>;
}) {
  const [accountNumber, setAccountNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!/^\d{10}$/.test(accountNumber)) return onError('Account number must be 10 digits');
    const amt = Number(amount);
    if (!amt || amt < 100) return onError('Amount must be at least ₦100');
    const pin = await requestPin('Send wallet transfer');
    if (pin === null) return;
    setSubmitting(true);
    const idempotencyKey = `txw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    try {
      const r: any = await postOwnerWalletTransferWallet({ amount: amt, account_number: accountNumber }, pin, idempotencyKey);
      onSuccess(r?.data?.message);
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Transfer to another wallet</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-3">
            <p className="text-xs text-slate-500">Available: <b className="text-slate-900 font-mono">{money(available)}</b></p>
            <p className="text-xs text-slate-500">
              Sends to another school's or user's XpressWallet NUBAN instantly.
            </p>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Recipient account number *</label>
              <input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                maxLength={10} placeholder="10 digits"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Amount *</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="Min ₦100"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
            <button onClick={submit} disabled={!accountNumber || !amount || submitting}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddBankModal({ banks, onClose, onSuccess, onError }: {
  banks: any[]; onClose: () => void; onSuccess: (msg?: string) => void; onError: (msg: string) => void;
}) {
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [purpose, setPurpose] = useState<'settlement' | 'payroll' | 'expense'>('settlement');
  const [isDefault, setIsDefault] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [letterFilter, setLetterFilter] = useState<string>('');

  // Filtered bank list — search + letter
  const filteredBanks = banks.filter((b: any) => {
    const name = String(b.name ?? '').toUpperCase();
    if (letterFilter && !name.startsWith(letterFilter)) return false;
    if (bankSearch) {
      const q = bankSearch.toUpperCase();
      if (!name.includes(q) && !String(b.code ?? '').includes(q)) return false;
    }
    return true;
  });

  const selectedBank = banks.find((b: any) => b.code === bankCode);

  const verify = async () => {
    if (!bankCode) return onError('Pick a bank first');
    if (!/^\d{10}$/.test(accountNumber)) return onError('Account number must be 10 digits');
    setVerifying(true);
    try {
      const r: any = await postSchoolBankAccountVerify({ bank_code: bankCode, account_number: accountNumber });
      const d = unwrap(r);
      const name = d?.account_name ?? d?.name ?? (typeof d === 'string' ? d : '');
      if (!name) return onError('Could not resolve account name');
      setAccountName(name);
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const submit = async () => {
    if (!accountName) return onError('Verify account first');
    setSubmitting(true);
    try {
      await postSchoolBankAccount({
        bank_name: selectedBank?.name ?? '',
        bank_code: bankCode,
        account_number: accountNumber,
        account_name: accountName,
        purpose,
        is_default: isDefault,
      });
      onSuccess();
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Add bank account</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Bank *</label>

              {/* Search input */}
              <input
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
                placeholder="Search bank name or code…"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2"
              />

              {/* A-Z letter filter */}
              <div className="flex flex-wrap gap-0.5 mb-2 select-none">
                <button
                  type="button"
                  onClick={() => setLetterFilter('')}
                  className={`px-2 py-0.5 text-[11px] font-bold rounded ${
                    letterFilter === ''
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  ALL
                </button>
                {['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'].map(L => (
                  <button
                    key={L}
                    type="button"
                    onClick={() => setLetterFilter(letterFilter === L ? '' : L)}
                    className={`w-6 h-6 text-[11px] font-bold rounded ${
                      letterFilter === L
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {L}
                  </button>
                ))}
              </div>

              {/* Dropdown */}
              <select value={bankCode} onChange={(e) => { setBankCode(e.target.value); setAccountName(''); }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">
                  {filteredBanks.length === 0
                    ? 'No banks match — clear filters'
                    : `Choose from ${filteredBanks.length} bank${filteredBanks.length === 1 ? '' : 's'}…`}
                </option>
                {filteredBanks.map((b: any) => (
                  <option key={b.code ?? b.id} value={b.code}>{b.name} · {b.code}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                {banks.length} total banks · {filteredBanks.length} shown
              </p>
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Account number *</label>
                <input value={accountNumber} onChange={(e) => { setAccountNumber(e.target.value.replace(/\D/g, '')); setAccountName(''); }}
                  maxLength={10} placeholder="10 digits"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <button onClick={verify} disabled={verifying || !bankCode || accountNumber.length !== 10}
                className="px-3 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {verifying ? 'Verifying…' : 'Verify'}
              </button>
            </div>
            {accountName && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">✓ {accountName}</p>
            )}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Purpose</label>
              <select value={purpose} onChange={(e) => setPurpose(e.target.value as any)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="settlement">Settlement</option>
                <option value="payroll">Payroll</option>
                <option value="expense">Expenses</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              Set as default bank account
            </label>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
            <button onClick={submit} disabled={!accountName || submitting}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {submitting ? 'Saving…' : 'Save account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequestWithdrawalModal({ bankAccounts, available, onClose, onSuccess, onError, requestPin }: {
  bankAccounts: any[]; available: number; onClose: () => void; onSuccess: (msg?: string) => void; onError: (msg: string) => void;
  requestPin: (title: string) => Promise<string | null>;
}) {
  const [bankAccountId, setBankAccountId] = useState(bankAccounts[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!bankAccountId) return onError('Pick a bank account');
    const amt = Number(amount);
    if (!amt || amt < 100) return onError('Amount must be at least ₦100');
    if (amt > available) return onError(`Not enough available balance (max ${money(available)})`);
    const pin = await requestPin('Request withdrawal');
    if (pin === null) return;
    setSubmitting(true);
    const idempotencyKey = `wd-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    try {
      const r: any = await postSchoolWithdrawal({
        bank_account_id: Number(bankAccountId),
        amount: amt,
      }, pin, idempotencyKey);
      onSuccess(r?.data?.message);
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Withdrawal failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Request withdrawal</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-3">
            <p className="text-xs text-slate-500">Available for withdrawal: <b className="text-slate-900 font-mono">{money(available)}</b></p>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Destination bank account *</label>
              <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Choose account…</option>
                {bankAccounts.map((a: any) => (
                  <option key={a.id} value={a.id}>
                    {a.bank_name} · {a.account_number} ({a.account_name})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Amount *</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="Min ₦100"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              Owner-requested withdrawals are auto-approved and queued to the bank immediately.
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
            <button onClick={submit} disabled={!bankAccountId || !amount || submitting}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {submitting ? 'Requesting…' : 'Request withdrawal'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TransactionDetailModal({ id, onClose }: { id: number; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['owner-wallet-tx-detail', id],
    queryFn: () => getOwnerWalletTransaction(id).then(unwrap),
  });
  const tx = q.data;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Transaction detail</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-2 text-sm">
            {q.isLoading ? (
              <p className="text-slate-500">Loading…</p>
            ) : !tx ? (
              <p className="text-red-600">Could not load transaction.</p>
            ) : (
              <>
                <Row label="Reference" value={tx.reference} mono />
                <Row label="Narration" value={tx.narration ?? '—'} />
                <Row label="Type" value={tx.type} />
                <Row label="Amount" value={money(tx.amount)} mono />
                <Row label="Status" value={tx.status} />
                <Row label="Date" value={fmtDate(tx.created_at)} />
                {tx.initiated_by && <Row label="Initiated by" value={tx.initiated_by.name} />}
                {tx.related_type && <Row label="Related to" value={`${tx.related_type} #${tx.related_id}`} />}
                {tx.metadata && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-semibold text-slate-500">Raw metadata</summary>
                    <pre className="text-xs bg-slate-50 p-2 rounded mt-1 overflow-x-auto">
                      {JSON.stringify(tx.metadata, null, 2)}
                    </pre>
                  </details>
                )}
              </>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', maxLength, inputMode }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; maxLength?: number; inputMode?: 'numeric' | 'text';
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} maxLength={maxLength} inputMode={inputMode} autoComplete="off"
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-100">
      <span className="text-slate-500">{label}</span>
      <span className={`text-slate-900 font-medium ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}


function PinSetupModal({ hasPin, onClose, onSuccess, onError }: {
  hasPin: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [currentPin, setCurrentPin] = useState('');
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (hasPin && !/^\d{4}$/.test(currentPin)) return onError('Enter your current 4-digit PIN');
    if (!/^\d{4}$/.test(pin)) return onError('New PIN must be exactly 4 digits');
    if (pin !== confirm) return onError('PINs do not match');
    if (['1234', '0000', '1111'].includes(pin)) return onError('Choose a stronger PIN');
    setSubmitting(true);
    try {
      if (hasPin) {
        await postAuthPinChange({ current_pin: currentPin, new_pin: pin, new_pin_confirmation: confirm });
      } else {
        await postAuthPinSet({ pin, pin_confirmation: confirm });
      }
      onSuccess();
    } catch (e: any) {
      const msg = e?.response?.data?.message
        || (e?.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' · '))
        || 'PIN setup failed';
      onError(String(msg));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
          <div>
            <h3 className="font-bold text-slate-900">{hasPin ? 'Change PIN' : 'Set PIN'}</h3>
            <p className="text-xs text-slate-500">4 digits, required for withdrawals and transfers</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          {hasPin && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Current PIN *</label>
              <input type="password" inputMode="numeric" value={currentPin}
                onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="Your current 4 digits" maxLength={4}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono text-center text-2xl tracking-widest" />
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">New PIN *</label>
            <input type="password" inputMode="numeric" value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="4 digits" maxLength={4}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono text-center text-2xl tracking-widest" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Confirm PIN *</label>
            <input type="password" inputMode="numeric" value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="4 digits" maxLength={4}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono text-center text-2xl tracking-widest" />
          </div>
          <p className="text-xs text-slate-500">
            Do not use 1234, 0000, or your birth year.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
          <button onClick={onClose}
            className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting || pin.length !== 4 || confirm.length !== 4 || (hasPin && currentPin.length !== 4)}
            className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
            {submitting ? 'Saving…' : hasPin ? 'Change PIN' : 'Set PIN'}
          </button>
        </div>
      </div>
    </div>
  );
}


function LinkExistingWalletModal({ onClose, onSuccess, onError }: {
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [accountNumber, setAccountNumber] = useState('');
  const [found, setFound] = useState<any | null>(null);
  const [finding, setFinding] = useState(false);
  const [linking, setLinking] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const findAccount = async () => {
    if (accountNumber.length !== 10) {
      setMsg('Enter a 10-digit account number');
      return;
    }
    setFinding(true);
    setMsg(null);
    setFound(null);
    try {
      const r: any = await getWalletFindAccount(accountNumber);
      const d = unwrap(r);
      setFound(d);
      setMsg('Account found. Confirm below to link it to this school.');
    } catch (e: any) {
      setFound(null);
      setMsg(e?.response?.data?.message ?? 'No Xpress account found with that number');
    } finally {
      setFinding(false);
    }
  };

  const linkAccount = async () => {
    if (!found) return;
    setLinking(true);
    setMsg(null);
    try {
      await postWalletLink(accountNumber);
      onSuccess();
    } catch (e: any) {
      setMsg(e?.response?.data?.message ?? 'Link failed');
      onError(e?.response?.data?.message ?? 'Link failed');
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <div>
              <h3 className="font-bold text-slate-900">Link existing Xpress account</h3>
              <p className="text-xs text-slate-500">Enter an Xpress wallet number you already own</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Xpress account number *</label>
              <input
                value={accountNumber}
                onChange={(e) => {
                  setAccountNumber(e.target.value.replace(/\D/g, ''));
                  setFound(null);
                  setMsg(null);
                }}
                maxLength={10}
                placeholder="10 digits from your Xpress app"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono text-center text-lg tracking-widest"
              />
              <p className="text-xs text-slate-500 mt-1">
                Find it inside the Xpress Wallet app or ask Xpress support. Do NOT use a personal account you also use for other purposes.
              </p>
            </div>

            <button
              onClick={findAccount}
              disabled={finding || accountNumber.length !== 10}
              className="w-full py-2.5 bg-slate-900 text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {finding ? 'Searching...' : 'Find account'}
            </button>

            {msg && (
              <div className={
                found
                  ? 'bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800'
                  : 'bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800'
              }>
                {msg}
              </div>
            )}

            {found && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Name</span><span className="font-semibold text-slate-900">{found.name || '-'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Account</span><span className="font-mono text-slate-900">{found.account_number}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="text-slate-700">{found.email || '-'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Balance</span><span className="font-mono text-slate-900">{money(found.balance)}</span></div>
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">
              Cancel
            </button>
            <button onClick={linkAccount}
              disabled={!found || linking}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {linking ? 'Linking...' : 'Link this account'}
            </button>
          </div>
        </div>
      </div>
    </div>);
}


function UnlinkXpressModal({
  accountNumber,
  balance,
  onClose,
  onSuccess,
}: {
  accountNumber: string;
  balance: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await postOwnerWalletUnlink();
      onSuccess();
    } catch (e: any) {
      setMsg(e?.response?.data?.message ?? 'Unlink failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">Unlink Xpress account</h3>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-slate-700">
            You're about to disconnect{' '}
            <span className="font-mono font-semibold">{accountNumber || '—'}</span>{' '}
            from this school.
          </p>
          {balance > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <b>Heads up:</b> ₦
              {balance.toLocaleString('en-NG', { minimumFractionDigits: 2 })}{' '}
              is sitting in that Xpress wallet. Transfer it out via the Xpress app
              before unlinking — this app won't be able to reach it once unlinked.
            </div>
          )}
          <p className="text-sm text-slate-600">
            After unlinking you can link a different Xpress account or create a
            brand-new wallet.
          </p>
          {msg && <p className="text-sm text-red-600">{msg}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50"
          >
            {busy ? 'Unlinking…' : 'Unlink now'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PinModal({ title, onClose, onConfirm }: {
  title: string;
  onClose: () => void;
  onConfirm: (pin: string) => void;
}) {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const submit = () => {
    if (!/^\d{4}$/.test(pin)) { setErr('Enter 4 digits'); return; }
    onConfirm(pin);
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Enter your 4-digit transaction PIN</p>
        </div>
        <div className="p-6">
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            autoFocus
            value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setErr(''); }}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            className="w-full text-center text-3xl tracking-[0.5em] font-mono border-2 border-slate-300 rounded-xl py-3 focus:border-slate-900 focus:outline-none"
          />
          {err && <p className="text-sm text-red-600 mt-2 text-center">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm text-slate-700">Cancel</button>
          <button onClick={submit} disabled={pin.length !== 4} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">Confirm</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel = 'Confirm', onClose, onConfirm }: {
  title: string; message: string; confirmLabel?: string;
  onClose: () => void; onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">{title}</h3>
        </div>
        <div className="p-6 text-sm text-slate-700">{message}</div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm text-slate-700">Cancel</button>
          <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-semibold text-sm">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function TextPromptModal({ title, label, placeholder, required, onClose, onSubmit }: {
  title: string; label: string; placeholder?: string; required?: boolean;
  onClose: () => void; onSubmit: (text: string) => void;
}) {
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const submit = () => {
    if (required && !text.trim()) { setErr('This field is required'); return; }
    onSubmit(text);
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">{title}</h3>
        </div>
        <div className="p-6 space-y-2">
          <label className="block text-sm font-semibold text-slate-700">{label}</label>
          <input
            autoFocus
            value={text}
            onChange={(e) => { setText(e.target.value); setErr(''); }}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder={placeholder}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm text-slate-700">Cancel</button>
          <button onClick={submit} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm">Submit</button>
        </div>
      </div>
    </div>
  );
}
